import {
  SimpleGrid,
  Input,
  VStack,
  Heading,
  Button,
  InputRightAddon,
  InputGroup,
  useToast,
  chakra,
  Radio,
  RadioGroup,
  Box,
  Switch,
  Flex,
  Text,
  Divider,
} from '@chakra-ui/react'
import { Controller, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '../../../app/typedReduxHooks'
import { RootState } from '../../../app/store'
import { STATUS } from '../../../constants'
import InputField from '../../../components/InputField'
import { updateOrganizationSettingsData } from '../OrganizationSlice'
import { FundingSource, OrganizationSetting } from '../../../types/organization'

const FormWithStyle = chakra('form')

type OrgSettingSubset = Pick<
  OrganizationSetting,
  'eRxTimeout' | 'idleTimeout' | 'defaultFundingSource' | 'enableSharePrescription'
>

const OrganizationSettings = () => {
  const dispatch = useAppDispatch()
  const toast = useToast()
  const organization = useSelector((state: RootState) => state.organization?.data)
  const { updateOrganizationSetting } = useSelector(
    (state: RootState) => state.organization?.status
  )
  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
  } = useForm<OrgSettingSubset>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  })

  if (!organization) {
    return null
  }

  const handleUpdate = (value: OrgSettingSubset) => {
    if (organization.id) {
      dispatch(
        updateOrganizationSettingsData({
          ...value,
          idleTimeout: value.idleTimeout * 60,
        })
      ).then((res) => {
        if (res.meta.requestStatus === 'fulfilled') {
          toast({
            title: 'Organization settings updated successfully',
            status: 'success',
            duration: 3000,
            position: 'top',
          })
        } else {
          toast({
            title: 'Failed to update organization settings, please try again.',
            status: 'error',
            duration: 3000,
            position: 'top',
          })
        }
      })
    }
  }

  const idleTimeout = organization?.settings?.idleTimeout
    ? Math.round(organization?.settings?.idleTimeout / 60)
    : 0

  return (
    <Box flex={1}>
      <Heading as="h3" size="md" mb={4} color="gray.800" fontWeight="bold">
        Organisation Setting
      </Heading>
      <FormWithStyle onSubmit={handleSubmit(handleUpdate)} w="100%">
        <VStack spacing={6} align="stretch" divider={<Divider borderColor="gray.100" />}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacingY={4} spacingX={6} w="100%">
            <InputField
              label="eRx Timeout"
              field="eRxTimeout"
              errors={errors}
              tooltipContent="0 and empty means no timeout"
              useTooltipIcon
            >
              <InputGroup size="md">
                <Input
                  type="number"
                  bg="white"
                  {...register('eRxTimeout', {
                    value: organization?.settings?.eRxTimeout,
                    setValueAs: (value) => (value ? Number(value) : 0),
                    validate: (value) => {
                      if (isNaN(value)) {
                        return 'The timeout value should be a number'
                      }
                    },
                  })}
                />
                <InputRightAddon children="seconds" bg="gray.50" />
              </InputGroup>
            </InputField>
            <InputField label="Inactivity Detection" field="idleTimeout" errors={errors}>
              <InputGroup size="md">
                <Input
                  type="number"
                  placeholder="Inactivity Detection"
                  bg="white"
                  {...register('idleTimeout', {
                    required: true,
                    value: idleTimeout,
                    setValueAs: (value) => (value ? Number(value) : 0),
                    validate: (value) => {
                      const timeout = Number(value)
                      if (isNaN(timeout)) {
                        return 'The inactivity detection timeout value should be a number'
                      }

                      if (timeout < 5 || timeout > 90) {
                        return 'Inactivity detection timeout must be between 300 seconds (5 minutes) and 5400 seconds (90 minutes), otherwise it will be 900 seconds (15 minutes) by default.'
                      }
                    },
                  })}
                />
                <InputRightAddon children="minutes" bg="gray.50" />
              </InputGroup>
            </InputField>
          </SimpleGrid>

          <Controller
            control={control}
            name="defaultFundingSource"
            defaultValue={organization?.settings?.defaultFundingSource ?? FundingSource.PRIVATE}
            render={({ field }) => (
              <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                <Box>
                  <Text fontWeight="medium" color="gray.800">
                    Default funding source
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Applied to new prescriptions by default
                  </Text>
                </Box>
                <RadioGroup {...field} display="flex" gap={6}>
                  <Radio value={FundingSource.PRIVATE} colorScheme="blue">
                    Private
                  </Radio>
                  <Radio value={FundingSource.PBS} colorScheme="blue">
                    PBS
                  </Radio>
                </RadioGroup>
              </Flex>
            )}
          />

          <Controller
            control={control}
            name="enableSharePrescription"
            defaultValue={organization?.settings?.enableSharePrescription ?? false}
            render={({ field: { onChange, value, ref } }) => (
              <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                <Box>
                  <Text fontWeight="medium" color="gray.800">
                    Enable Share Prescription
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Allow prescriptions to be shared with patients
                  </Text>
                </Box>
                <Switch
                  id="enableSharePrescription"
                  colorScheme="blue"
                  size="lg"
                  isChecked={value}
                  onChange={(e) => onChange(e.target.checked)}
                  ref={ref}
                  aria-label="Enable Share Prescription"
                />
              </Flex>
            )}
          />

          <Box>
            <Button
              colorScheme="blue"
              size="md"
              type="submit"
              px={8}
              isLoading={updateOrganizationSetting === STATUS.PENDING}
              loadingText="Submitting"
            >
              Submit
            </Button>
          </Box>
        </VStack>
      </FormWithStyle>
    </Box>
  )
}

export default OrganizationSettings
